/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { Colors, Icon, Typography } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { CustomButton } from '../../CustomButton';
import { COLOR_KEYS, ICON_NAMES, MEDIA_MIN_WIDTH } from '../../../constants';
import { SectionName, useAvroTracking } from '../../../observability';

export type CompactCTASectionProps = {
  backgroundColor: typeof COLOR_KEYS.cream | typeof COLOR_KEYS.zapier;
  ctaText: string;
  ctaUrl: string;
  heading: string;
  isFullWidth?: boolean;
};

const Styles = labelStyles('CompactCTASection', {
  root: (props: CompactCTASectionProps) =>
    css({
      backgroundColor: props.isFullWidth
        ? Colors[props.backgroundColor]
        : Colors.neutral100,
      ...(props.isFullWidth
        ? {
            padding: '0 20px',
          }
        : {
            padding: '20px',

            [MEDIA_MIN_WIDTH.small]: {
              padding: '64px 20px',
            },

            [MEDIA_MIN_WIDTH.large]: {
              padding: '120px 20px',
            },
          }),
    }),

  content: (props: CompactCTASectionProps) =>
    css({
      maxWidth: '1170px',
      margin: '0 auto',
      textAlign: 'center',

      ...(props.isFullWidth
        ? {
            padding: '48px 0',

            [MEDIA_MIN_WIDTH.small]: {
              padding: '60px 0',
            },

            [MEDIA_MIN_WIDTH.large]: {
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '120px 0',
              textAlign: 'left',
            },
          }
        : {
            padding: '48px 20px',
            backgroundColor: Colors[props.backgroundColor],
            borderRadius: '5px',

            [MEDIA_MIN_WIDTH.large]: {
              padding: '60px 20px',
            },
          }),
    }),

  heading: (props: CompactCTASectionProps) =>
    css({
      ...Typography.pageHeader5,
      maxWidth: '448px',
      margin: '0 auto 32px',
      color:
        props.backgroundColor === COLOR_KEYS.cream
          ? Colors.zapier
          : Colors.cream,

      [MEDIA_MIN_WIDTH.large]: {
        ...Typography.pageHeader4,
        maxWidth: 'none',
        margin: props.isFullWidth ? '0 48px 0 0' : '',
      },
    }),

  cta: css({
    width: '288px',
    minWidth: 'fit-content',
    maxWidth: '100%',
    margin: '0 auto',
  }),

  ctaText: css({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  }),
});

export function CompactCTASection(props: CompactCTASectionProps) {
  const { emitCTAClickEvent } = useAvroTracking();
  return (
    <div css={Styles.root(props)}>
      <div css={Styles.content(props)}>
        <h2 css={Styles.heading(props)}>{props.heading}</h2>
        <div css={Styles.cta}>
          <CustomButton
            color={
              props.backgroundColor === COLOR_KEYS.cream
                ? 'primary'
                : 'secondary-alt'
            }
            href={props.ctaUrl}
            isFullWidth={true}
            onClick={() =>
              emitCTAClickEvent({
                text: props.ctaText,
                href: props.ctaUrl,
                section: SectionName.CompactCTASection,
              })
            }
          >
            <span css={Styles.ctaText}>
              <span>{props.ctaText}</span>
              <Icon name={ICON_NAMES.arrowBigRight} size={24} />
            </span>
          </CustomButton>
        </div>
      </div>
    </div>
  );
}
